import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import GoogleSignInButton from "../components/GoogleSignInButton";
import Logo from "../components/Logo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Eye, EyeOff, Loader2, Check, X } from "lucide-react";
import { toast } from "sonner";
import api, { setToken, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * Signup — email + password account creation.
 *
 * Google is offered on the same page (fast path) so users who bounce here
 * from a search result don't have to backtrack to /login just to choose
 * Google. The form runs client-side validation for immediate feedback,
 * but the backend re-validates so we don't rely on it for security.
 */
export default function Signup() {
  const [form, setForm] = useState({ name: "", email: "", password: "", confirm: "" });
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Live password strength — matches the backend rule (min 8) + a nudge to
  // include some variety. Doesn't gate submit; feedback only.
  const rules = [
    { key: "len", label: "At least 8 characters", ok: form.password.length >= 8 },
    { key: "match", label: "Passwords match", ok: form.confirm && form.password === form.confirm },
  ];
  const allValid = form.name.trim().length > 0 && /\S+@\S+\.\S+/.test(form.email) && rules.every((r) => r.ok);

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (form.password !== form.confirm) {
      toast.error("Passwords don't match.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/register", {
        name: form.name.trim(),
        email: form.email.trim(),
        password: form.password,
      });
      if (data?.token) setToken(data.token);
      setUser(data);
      toast.success(`Welcome to Syllox, ${data.name?.split(" ")[0] || "there"}! 🎉`);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Sign up failed. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-zinc-50 dark:bg-zinc-950" data-testid="signup-root">
      <div className="pointer-events-none absolute inset-x-0 -top-40 -z-0 h-[420px] bg-[radial-gradient(circle_at_50%_0%,rgba(16,185,129,0.18),transparent_70%)]" aria-hidden="true"/>

      <div className="relative z-10 mx-auto flex min-h-screen max-w-md flex-col items-center px-6 py-10 sm:py-14">
        <Link to="/" className="flex items-center gap-2" data-testid="signup-logo">
          <Logo className="h-9 w-9" />
          <span className="font-heading text-lg font-bold">Syllox</span>
        </Link>

        <div className="mt-6 w-full">
          <h1 className="font-heading text-3xl font-bold leading-[1.05] tracking-tight sm:text-4xl" data-testid="signup-heading">
            Create your account.
          </h1>
          <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-400" data-testid="signup-subheading">
            30 days of free premium — no card required.
          </p>

          <div className="mt-6">
            <GoogleSignInButton testId="signup-google-button" />
          </div>

          <div className="my-5 flex items-center gap-3 text-[11px] uppercase tracking-wider text-zinc-400">
            <span className="h-px flex-1 bg-zinc-200 dark:bg-zinc-800"/>
            or with email
            <span className="h-px flex-1 bg-zinc-200 dark:bg-zinc-800"/>
          </div>

          <form onSubmit={submit} className="space-y-3" data-testid="signup-form">
            <div className="space-y-1.5">
              <Label htmlFor="signup-name">Name</Label>
              <Input
                id="signup-name"
                autoComplete="name"
                required
                maxLength={80}
                value={form.name}
                onChange={set("name")}
                placeholder="Your full name"
                data-testid="signup-name-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="signup-email">Email</Label>
              <Input
                id="signup-email"
                type="email"
                autoComplete="email"
                required
                value={form.email}
                onChange={set("email")}
                placeholder="you@example.com"
                data-testid="signup-email-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="signup-password">Password</Label>
              <div className="relative">
                <Input
                  id="signup-password"
                  type={showPw ? "text" : "password"}
                  autoComplete="new-password"
                  required
                  value={form.password}
                  onChange={set("password")}
                  className="pr-10"
                  data-testid="signup-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((s) => !s)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300"
                  aria-label={showPw ? "Hide password" : "Show password"}
                >
                  {showPw ? <EyeOff size={16}/> : <Eye size={16}/>}
                </button>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="signup-confirm">Confirm password</Label>
              <Input
                id="signup-confirm"
                type={showPw ? "text" : "password"}
                autoComplete="new-password"
                required
                value={form.confirm}
                onChange={set("confirm")}
                data-testid="signup-confirm-input"
              />
            </div>

            {/* Live rule feedback */}
            <ul className="mt-1 space-y-0.5 text-xs" data-testid="signup-rules">
              {rules.map((r) => (
                <li key={r.key} className={`flex items-center gap-1.5 ${r.ok ? "text-emerald-600" : "text-zinc-400"}`}>
                  {r.ok ? <Check size={12}/> : <X size={12}/>}
                  {r.label}
                </li>
              ))}
            </ul>

            <Button
              type="submit"
              disabled={submitting || !allValid}
              className="w-full h-11 bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-60"
              data-testid="signup-submit-btn"
            >
              {submitting ? <Loader2 size={16} className="animate-spin"/> : "Create account"}
            </Button>
          </form>

          <p className="mt-5 text-center text-sm text-zinc-600 dark:text-zinc-400">
            Already have an account?{" "}
            <Link to="/login" className="font-medium text-emerald-600 hover:underline" data-testid="signup-login-link">
              Log in
            </Link>
          </p>

          <p className="mt-4 text-center text-xs text-zinc-500 dark:text-zinc-400">
            By continuing, you agree to our{" "}
            <Link to="/terms" className="font-medium text-zinc-700 hover:text-emerald-600 dark:text-zinc-300">Terms</Link>
            {" "}and{" "}
            <Link to="/privacy" className="font-medium text-zinc-700 hover:text-emerald-600 dark:text-zinc-300">Privacy Policy</Link>.
          </p>
        </div>

        <div className="mt-auto pt-8 text-center text-xs text-zinc-400 dark:text-zinc-500">
          © {new Date().getFullYear()} Syllox · Made in India
        </div>
      </div>
    </div>
  );
}
